/**
 * NetworkPulse — quiet brand-symbol loading indicator at bottom-right.
 *
 * Listens to:
 *   - React Router navigation (Suspense-triggered chunk loads)
 *   - Axios in-flight count (any /api/* request)
 *
 * Shows a rotating orbit symbol with lime glow when anything is loading,
 * fades out gracefully when idle. Always-on, never blocking.
 */
import { useEffect, useState } from "react";
import { useLocation, useNavigationType } from "react-router-dom";
import { api } from "@/lib/api";
import BrandLogo from "@/components/BrandLogo";

let inflight = 0;
let listeners = [];
const notify = () => listeners.forEach((fn) => fn(inflight));

// Attach interceptors once (axios instance from lib/api)
(function attach() {
  if (api && api.interceptors && !api.__pulseAttached) {
    api.interceptors.request.use((cfg) => {
      inflight++; notify(); return cfg;
    });
    api.interceptors.response.use(
      (r) => { inflight = Math.max(0, inflight - 1); notify(); return r; },
      (e) => { inflight = Math.max(0, inflight - 1); notify(); return Promise.reject(e); },
    );
    api.__pulseAttached = true;
  }
})();

export default function NetworkPulse() {
  const [active, setActive] = useState(false);
  const [navTick, setNavTick] = useState(0);
  const location = useLocation();
  const navType = useNavigationType();

  // Track inflight axios requests
  useEffect(() => {
    const fn = (n) => setActive(n > 0);
    listeners.push(fn);
    return () => { listeners = listeners.filter((l) => l !== fn); };
  }, []);

  // Briefly flash on every route change (Suspense lazy-load feedback)
  useEffect(() => {
    setNavTick((t) => t + 1);
    setActive(true);
    const id = setTimeout(() => setActive(inflight > 0), 600);
    return () => clearTimeout(id);
  }, [location.pathname, navType]);

  return (
    <div
      data-testid="network-pulse"
      className={`network-pulse ${active ? "network-pulse-active" : ""}`}
      aria-hidden
    >
      <BrandLogo variant="symbol" size={20} className="brand-symbol-spin" />
      <span className="network-pulse-label">Network</span>
    </div>
  );
}
